import type { Injector } from '@angular/core';
import { type PlantDetailEntity, Vendors } from '@sds/api/administration/models';
import type {
  ComponentCommunicationError,
  DataTransferError,
  DeviationError,
  StatusError
} from '@sds/api/error-detection/models';
import { PlantResolver } from '@sds/shared/resolvers';
import { BaseWidget } from '@sds/widget-gridster';
import { first } from 'rxjs/operators';

export class ActionBarWidget extends BaseWidget<{
  plant: PlantDetailEntity;
  title: string;
  error: DeviationError | ComponentCommunicationError | DataTransferError | StatusError;
}> {
  static readonly id = 'action-bar';
  static readonly publicFriendly = false;

  static factory(
    injector: Injector,
    metaLinkId: string,
    x: number,
    y: number,
    rows: number,
    cols: number,
    data: { plantId: string; title: string }
  ): ActionBarWidget {
    return new ActionBarWidget(injector, metaLinkId, x, y, rows, cols, data, injector.get(PlantResolver));
  }

  constructor(
    injector: Injector,
    metaLinkId: string,
    x: number,
    y: number,
    rows: number,
    cols: number,
    data: { plantId: string; title: string },
    private readonly resolver: PlantResolver
  ) {
    super(injector, ActionBarWidget.id, ActionBarWidget.publicFriendly, metaLinkId, x, y, rows, cols, data);
  }

  override resolveData(data: {
    plant?: PlantDetailEntity;
    plantId?: string;
    title?: string;
    error?: DeviationError | ComponentCommunicationError | DataTransferError | StatusError;
  }): void {
    if (data.plant && data.error) {
      this.setData({ plant: data.plant, title: data.plant.label, error: data.error });
    } else if (data.plantId) {
      this.resolver
        .resolveByMetaId(data.plantId)
        .pipe(first())
        .subscribe(plant => {
          this.setData({ plant, title: data.title, error: data.error });
        });
    }
  }

  override setData(
    newData: Partial<{
      plant: PlantDetailEntity;
      title: string;
      error: DeviationError | ComponentCommunicationError | DataTransferError | StatusError;
    }>
  ): void {
    this.resolveComponent(async () => {
      const module =
        newData.plant?.vendor === Vendors.Vp
          ? await import('./vp-action-bar/vp-action-bar.component')
          : await import('./action-bar/action-bar.component');
      return Object.values(module)[0];
    });
    super.setData(newData);
  }
}
